import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { X, ChevronLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDismissal } from '../utils/dismissals';
import './da/kit.css';

// Visite guidée salarié (format site) : badge noir penché · tag pastel · titre serif · tags · boutons. Zéro phrase.
// Déclenchée au premier login d'un salarié (pas direction) ; rejouable depuis Plus → « Revoir le guide » (forceShow).

const ROLE_GROUP = { serveur: 'salle', barman: 'salle', barrista: 'salle', hote: 'salle', runner: 'salle', commis: 'cuisine', chef_cuisine: 'cuisine', chef: 'cuisine', second_cuisine: 'cuisine', plonge: 'cuisine' };

const isIOSSafari = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
const isAndroidChrome = () => /Android/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);

const POINTAGE = { tag: 'Pointage', tone: 'mint', title: 'Pointez votre arrivée', tags: ['Arrivée = 1 tap', 'Départ = 1 tap', 'Heures & primes comptées'], go: 'pointage', action: 'Ouvrir le pointage' };
const BASECAMP = { tag: 'Formation', tone: 'rose', title: 'Formation', tags: ['3 familles', '1 niveau par jour', 'Certificat'], go: 'formation', action: 'Voir ma formation' };

const STEPS_SALLE = [
  { tag: 'Bienvenue', tone: 'sun', title: 'Votre espace', tags: ['Pointage', 'Planning', 'Tâches', 'Primes'] },
  POINTAGE,
  { tag: 'Planning', tone: 'lilas', title: 'Vos horaires de la semaine', tags: ['Semaine en un coup d’œil', 'Changement de créneau'], go: 'planning', action: 'Voir mon planning' },
  { tag: 'Tâches', tone: 'sun', title: 'Vos tâches du jour', tags: ['Checklist par service', 'Cochez au fil de l’eau', 'XP à chaque validation'], go: 'taches', action: 'Voir mes tâches' },
  BASECAMP,
];

const STEPS_CUISINE = [
  { tag: 'Bienvenue', tone: 'sun', title: 'Bienvenue chef', tags: ['Fiches', 'HACCP', 'Tâches', 'Formation'] },
  POINTAGE,
  { tag: 'HACCP', tone: 'rose', title: 'HACCP au quotidien', tags: ['Relevés T°', 'Étiquettes DLC', '1 minute par jour'], go: 'haccp', action: 'Ouvrir HACCP' },
  { tag: 'Fiches', tone: 'mint', title: 'Fiches techniques', tags: ['Grammages exacts', 'Coût recalculé'], go: 'fiches', action: 'Voir les fiches' },
  BASECAMP,
];

const buildPWAStep = () => {
  if (isIOSSafari()) return { tag: 'Installer', tone: 'sky', title: 'L’app sur votre iPhone', tags: ['Safari · Partager', '« Sur l’écran d’accueil »', 'Ajouter'], last: 'C’est parti' };
  if (isAndroidChrome()) return { tag: 'Installer', tone: 'sky', title: 'L’app sur votre Android', tags: ['Chrome · menu ⋮', '« Installer l’application »', 'Confirmer'], last: 'C’est parti' };
  return { tag: 'Installer', tone: 'sky', title: 'L’app sur votre ordinateur', tags: ['Chrome / Edge', 'Icône + dans la barre d’adresse', 'Installer'], last: 'C’est parti' };
};

export const EmployeeFirstLoginTour = ({ forceShow = false, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  const steps = useMemo(() => {
    const group = ROLE_GROUP[(user?.role || '').toLowerCase()] || 'salle';
    return [...(group === 'cuisine' ? STEPS_CUISINE : STEPS_SALLE), buildPWAStep()];
  }, [user?.role]);

  const { ready: dismissReady, dismissed: serverDismissed, dismiss: serverDismiss } = useDismissal('employee_tour');

  useEffect(() => {
    if (!user) return;
    if (!dismissReady && !forceShow) return;
    const isOwnerLike = ['owner', 'manager', 'admin', 'patron', 'directeur'].includes((user.role || '').toLowerCase());
    if (isOwnerLike && !forceShow) return;
    const done = !forceShow && (serverDismissed || localStorage.getItem(`emp_tour_done_${user.user_id}`) === '1');
    setVisible(!done);
  }, [user, forceShow, dismissReady, serverDismissed]);

  const finish = () => {
    if (user) localStorage.setItem(`emp_tour_done_${user.user_id}`, '1');
    serverDismiss('forever');
    setVisible(false);
    onClose?.();
  };
  const later = () => { serverDismiss('later'); setVisible(false); onClose?.(); };
  const next = () => (step >= steps.length - 1 ? finish() : setStep((s) => s + 1));
  const go = (path) => { finish(); navigate(`/${tenantSlug}/${path}`); };

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') later(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible || !user || user.demo_mode) return null;
  const cur = steps[step];
  const isLast = step === steps.length - 1;

  return createPortal(
    <div className="kit-overlay" onClick={later} data-testid="employee-first-login-tour">
      <div className="kit-modal app-da emp-tour" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} data-testid="emp-tour-card">
        <button type="button" className="kit-modal-x" onClick={finish} aria-label="Fermer" data-testid="emp-tour-close"><X size={16} /></button>
        <div className="kit-card emp-tour-step" key={step}>
          <span className="kit-card-badge" data-testid="emp-tour-step">{step + 1}</span>
          <span className={`kit-tag is-${cur.tone} kit-card-tag`}>{cur.tag}</span>
          <b className="kit-card-title" data-testid="emp-tour-title">{cur.title}<span className="kit-dot">.</span></b>
          <div className="emp-tour-tags" data-testid="emp-tour-body">{cur.tags.map((t) => <span key={t} className="kit-tag is-gris">{t}</span>)}</div>
          {cur.go && <button type="button" className="btn-kit btn-kit-light" onClick={() => go(cur.go)} data-testid="emp-tour-go">{cur.action}</button>}
        </div>
        <div className="emp-tour-dots" aria-hidden="true">{steps.map((_, i) => <i key={i} className={i === step ? 'is-on' : ''} />)}</div>
        <div className="emp-tour-actions">
          <button type="button" className="btn-kit btn-kit-light" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} aria-label="Précédent" data-testid="emp-tour-prev"><ChevronLeft size={16} /></button>
          <button type="button" className="btn-kit btn-kit-light" onClick={later} data-testid="emp-tour-later">Plus tard</button>
          <button type="button" className="btn-kit btn-kit-light" onClick={finish} data-testid="emp-tour-skip">Ne plus montrer</button>
          <button type="button" className="btn-kit btn-kit-dark emp-tour-next" onClick={next} data-testid="emp-tour-next">{isLast ? cur.last || 'Terminer' : 'Suivant'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EmployeeFirstLoginTour;
